import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, CardActions,
    Button, Chip, CircularProgress, Alert, Dialog,
    DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AppsIcon from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import Can from '../Can';
import StudentManageAppVolPage from './StudentManageAppVolPage';

function PredefinedLabsList({ apiAuthResponse, apiAuthGroups }) {
    const [labs, setLabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [enrollingLab, setEnrollingLab] = useState(null);
    const [enrollResult, setEnrollResult] = useState(null);
    const [selectedLab, setSelectedLab] = useState(null);
    const [isShowAppVol, setIsShowAppVol] = useState(false);
    const [unenrollingLab, setUnenrollingLab] = useState(null);
    const [confirmUnenroll, setConfirmUnenroll] = useState(null);
    const [catalogPackages, setCatalogPackages] = useState([]);
    const [previewAppsLab, setPreviewAppsLab] = useState(null);

    const fetchLabs = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(process.env.REACT_APP_URL_GET_PREDEFINED_LABS, {
                credentials: 'include',
            });
            if (!response.ok) throw new Error('Errore nel recupero dei laboratori');
            const data = await response.json();
            setLabs(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLabs();
    }, [fetchLabs]);

    useEffect(() => {
        const fetchCatalog = async () => {
            try {
                const response = await fetch(process.env.REACT_APP_URL_GET_PREDEFINED_APPVOL_CATALOG, {
                    credentials: 'include',
                });
                const data = await response.json();
                if (data.statusRequest === 'success') {
                    setCatalogPackages(data.packages || []);
                }
            } catch (err) {
                // catalogo non disponibile, il chip non verrà mostrato
            }
        };
        fetchCatalog();
    }, []);

    const handleEnroll = async (idLab) => {
        setEnrollingLab(idLab);
        setEnrollResult(null);
        try {
            const response = await fetch(process.env.REACT_APP_URL_SELF_ENROLL_PREDEFINED, {
                credentials: 'include',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idLab }),
            });
            const data = await response.json();
            if (data.statusRequest === 'success') {
                setEnrollResult({ type: 'success', message: 'Iscrizione ricevuta! L\'attivazione avverrà entro qualche minuto.' });
                fetchLabs();
            } else {
                setEnrollResult({ type: 'error', message: data.message || 'Errore nell\'iscrizione' });
            }
        } catch (err) {
            setEnrollResult({ type: 'error', message: 'Errore di rete' });
        } finally {
            setEnrollingLab(null);
        }
    };

    const handleUnenroll = async (idLab) => {
        setUnenrollingLab(idLab);
        setEnrollResult(null);
        try {
            const response = await fetch(process.env.REACT_APP_URL_SELF_UNENROLL_PREDEFINED, {
                credentials: 'include',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idLab }),
            });
            const data = await response.json();
            if (data.statusRequest === 'success') {
                setEnrollResult({ type: 'success', message: 'Disiscrizione ricevuta! La rimozione avverrà entro qualche minuto.' });
                fetchLabs();
            } else {
                setEnrollResult({ type: 'error', message: data.message || 'Errore nella disiscrizione' });
            }
        } catch (err) {
            setEnrollResult({ type: 'error', message: 'Errore di rete' });
        } finally {
            setUnenrollingLab(null);
            setConfirmUnenroll(null);
        }
    };

    const handleOpenAppVol = (lab) => {
        setSelectedLab(lab);
        setIsShowAppVol(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#a71e3b' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#a71e3b' }}>
                Laboratori Predefiniti
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                Iscriviti ai laboratori virtuali predefiniti e gestisci le tue applicazioni.
            </Typography>

            {enrollResult && (
                <Alert severity={enrollResult.type} sx={{ mb: 2 }} onClose={() => setEnrollResult(null)}>
                    {enrollResult.message}
                </Alert>
            )}

            {labs.length === 0 ? (
                <Alert severity="info">Nessun laboratorio predefinito disponibile al momento.</Alert>
            ) : (
                <Grid container spacing={3}>
                    {labs.map(lab => (
                        <Grid item xs={12} sm={6} md={4} key={lab.idLab}>
                            <Card
                                elevation={0}
                                sx={{
                                    border: '1px solid',
                                    borderColor: lab.isEnrolled ? '#a71e3b' : '#e0e0e0',
                                    borderRadius: '12px',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-2px)',
                                    }
                                }}
                            >
                                <CardContent sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', wordBreak: 'break-word' }}>
                                                {lab.labName}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                ID: {lab.idLab}
                                            </Typography>
                                        </Box>
                                        {lab.isEnrolled && (
                                            <Chip
                                                icon={<CheckCircleIcon />}
                                                label="Iscritto"
                                                size="small"
                                                sx={{ bgcolor: 'rgba(167, 30, 59, 0.1)', color: '#a71e3b', fontWeight: 600 }}
                                            />
                                        )}
                                    </Box>

                                    {lab.labDescription && (
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                            {lab.labDescription}
                                        </Typography>
                                    )}

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ComputerIcon sx={{ fontSize: '1rem', color: '#888' }} />
                                            <Typography variant="caption" color="textSecondary">
                                                {lab.baseImage?.os} &mdash; {lab.baseImage?.name}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <GroupIcon sx={{ fontSize: '1rem', color: '#888' }} />
                                            <Typography variant="caption" color="textSecondary">
                                                {lab.enrollmentCount} iscritti
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {lab.baseImage?.hasAppVolAgent && catalogPackages.length > 0 && (
                                        <Chip
                                            icon={<AppsIcon />}
                                            label={`${catalogPackages.length} app disponibili`}
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); setPreviewAppsLab(lab); }}
                                            sx={{
                                                mt: 1.5,
                                                bgcolor: 'rgba(25, 118, 210, 0.08)',
                                                color: '#1565c0',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.16)' },
                                                '& .MuiChip-icon': { color: '#1565c0' },
                                            }}
                                        />
                                    )}
                                </CardContent>

                                <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                                    {!lab.isEnrolled ? (
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={() => handleEnroll(lab.idLab)}
                                            disabled={enrollingLab === lab.idLab}
                                            sx={{
                                                bgcolor: '#a71e3b',
                                                '&:hover': { bgcolor: '#8a1830' },
                                                textTransform: 'none',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {enrollingLab === lab.idLab ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : 'Iscriviti'}
                                        </Button>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                                            {lab.baseImage?.hasAppVolAgent && (
                                                <>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        startIcon={<Inventory2Icon />}
                                                        onClick={() => handleOpenAppVol(lab)}
                                                        sx={{
                                                            borderColor: '#a71e3b',
                                                            color: '#a71e3b',
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            '&:hover': { borderColor: '#8a1830', bgcolor: 'rgba(167, 30, 59, 0.04)' },
                                                        }}
                                                    >
                                                        Gestisci App
                                                    </Button>
                                                    {lab.appVolProvisioningStatus === 'queued' && (
                                                        <Chip icon={<HourglassEmptyIcon />} label="App in coda" size="small"
                                                            sx={{ bgcolor: 'rgba(237, 108, 2, 0.1)', color: 'warning.main', fontWeight: 600 }} />
                                                    )}
                                                    {lab.appVolProvisioningStatus === 'processing' && (
                                                        <Chip icon={<HourglassEmptyIcon />} label="Assegnazione in corso" size="small"
                                                            sx={{ bgcolor: 'rgba(237, 108, 2, 0.1)', color: 'warning.main', fontWeight: 600 }} />
                                                    )}
                                                    {lab.appVolProvisioningStatus === 'error' && (
                                                        <Chip icon={<ErrorOutlineIcon />} label="Errore assegnazione" size="small"
                                                            sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', color: 'error.main', fontWeight: 600 }} />
                                                    )}
                                                </>
                                            )}
                                            <Button
                                                variant="text"
                                                fullWidth
                                                size="small"
                                                onClick={() => setConfirmUnenroll(lab)}
                                                disabled={unenrollingLab === lab.idLab}
                                                sx={{
                                                    color: '#888',
                                                    textTransform: 'none',
                                                    fontSize: '0.8rem',
                                                    '&:hover': { color: '#d32f2f', bgcolor: 'rgba(211, 47, 47, 0.04)' },
                                                }}
                                            >
                                                {unenrollingLab === lab.idLab ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : 'Disiscrivi'}
                                            </Button>
                                        </Box>
                                    )}
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog open={!!previewAppsLab} onClose={() => setPreviewAppsLab(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                    <Box>
                        <Typography variant="h6" component="span" sx={{ fontWeight: 'bold', color: '#a71e3b' }}>
                            App disponibili
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {previewAppsLab?.labName}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setPreviewAppsLab(null)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Queste sono le applicazioni che potrai installare dopo l'iscrizione al laboratorio.
                    </Typography>
                    <Grid container spacing={2}>
                        {catalogPackages.map((pkg, index) => (
                            <Grid item xs={12} sm={6} md={4} key={pkg.sourceDetail?.applicationId || index}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '12px',
                                        height: '100%',
                                    }}
                                >
                                    <CardContent sx={{ pb: '12px !important' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Inventory2Icon sx={{ color: '#888', fontSize: '1.2rem', mt: 0.25, flexShrink: 0 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 600, color: '#333', wordBreak: 'break-word' }}
                                                >
                                                    {pkg.label || pkg.value}
                                                </Typography>
                                                {pkg.version && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        v{pkg.version}
                                                    </Typography>
                                                )}
                                                {pkg.description && (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        sx={{
                                                            mt: 0.5,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            fontSize: '0.8rem',
                                                            lineHeight: 1.4,
                                                        }}
                                                    >
                                                        {pkg.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewAppsLab(null)} variant="outlined" sx={{ textTransform: 'none' }}>
                        Chiudi
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!confirmUnenroll} onClose={() => setConfirmUnenroll(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Conferma disiscrizione</DialogTitle>
                <DialogContent>
                    <Typography>
                        Sei sicuro di volerti disiscrivere dal laboratorio <strong>{confirmUnenroll?.labName}</strong>?
                        Perderai l'accesso alle macchine virtuali e alle app assegnate.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmUnenroll(null)} color="primary" variant="outlined">
                        Annulla
                    </Button>
                    <Button
                        onClick={() => handleUnenroll(confirmUnenroll?.idLab)}
                        color="error"
                        variant="contained"
                        disabled={!!unenrollingLab}
                    >
                        {unenrollingLab ? <CircularProgress size={20} color="inherit" /> : 'Disiscrivi'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog fullScreen open={isShowAppVol} onClose={() => setIsShowAppVol(false)}>
                {isShowAppVol && selectedLab && (
                    <StudentManageAppVolPage
                        labProps={selectedLab}
                        setIsShowAppVol={setIsShowAppVol}
                        onSaved={fetchLabs}
                    />
                )}
            </Dialog>
        </Box>
    );
}

function PredefinedLabsPage(props) {
    return (
        <Can
            role={props.apiAuthGroups}
            perform="labPredefiniti-page:visit"
            yes={() => (
                <PredefinedLabsList
                    apiAuthResponse={props.apiAuthResponse}
                    apiAuthGroups={props.apiAuthGroups}
                />
            )}
            no={() => null}
        />
    );
}

export default PredefinedLabsPage;
